/**
 * @file shop-types.ts
 *
 * Domain entity types for the shop admin, mirroring the design-reviewed Website
 * Builder v2 shapes so the ported screens and stores stay close to the
 * reference. These describe what the screens consume; the {@link WcResult}
 * envelope and {@link WcApiError} (in `types.ts`) describe how it is delivered.
 * Today these are served by MSW fixtures; when the custom commerce API lands a
 * mapping adapter produces the same shapes and the screens stay untouched.
 */

/** WooCommerce-style product type. */
export enum WooProductType {
  Simple = 'simple',
  Variable = 'variable',
  Grouped = 'grouped',
  External = 'external',
}

/**
 * One value of a variation axis, carrying its commerce-engine value id when known.
 * `id` is present for a value read back from the engine (so a save can rename it in
 * place — the engine matches by id and preserves the variant, its price/stock/SKU, and
 * any existing carts); `id` is absent for a value the merchant just typed (a new value,
 * which the engine inserts). Reported to the wire as `{ id, value }` when an id is
 * present and as a bare label otherwise (see `toVariationAxes` in `products.ts`).
 */
export interface ShopOptionValue {
  id?: string;
  value: string;
}

/** A configurable product attribute (e.g. "Size" → ["S", "M", "L"]). */
export interface ShopProductAttribute {
  id: string;
  name: string;
  slug: string;
  position: number;
  visible: boolean;
  variation: boolean;
  /** The axis's ordered values; each keeps its engine value id when read from a product. */
  options: ShopOptionValue[];
}

/**
 * The admin-only sellable-stock rollup carried on a {@link ShopProduct}, over the
 * product's ENABLED variants. `lowestQuantity` is `null` when no variant is tracked;
 * `trackedQuantitySum` is the total sellable tracked quantity (the `(n)` on the list).
 * Drives the list/drawer stock badge severity and count. Mirrors the `/v1`
 * `stockSummary`; absent (`null`) on a summary that predates the aggregate, when the
 * badge falls back to the raw product-level inventory.
 */
export interface ShopStockSummary {
  lowestQuantity: number | null;
  trackedQuantitySum: number;
  outOfStockCount: number;
  inStockCount: number;
  trackedCount: number;
}

/**
 * A single generated variant as the admin form consumes it — one row of the
 * per-variant table. `options` are the combination selectors ({@link ShopProductAttribute}
 * name + the chosen value LABEL, resolved from the wire's option-value ids); `label` is
 * the joined display label (e.g. "M / Red"). Money/weight are the same display strings
 * the rest of {@link ShopProduct} uses (decimal price, kilogram weight). `stock_quantity`
 * is `null` when the variant is untracked (unlimited stock). `enabled` is the retire flag.
 */
export interface ShopProductVariant {
  id: string;
  options: { name: string; value: string }[];
  label: string;
  regular_price: string;
  sale_price: string;
  sku: string;
  weight: string;
  manage_stock: boolean;
  stock_quantity: number | null;
  image: { id: string; src: string; alt: string } | null;
  enabled: boolean;
}

/**
 * A product as consumed by the admin screens. Textual fields (`name`,
 * `description`, `short_description`) are plain text once normalized into the
 * products store.
 */
export interface ShopProduct {
  id: string;
  name: string;
  type: WooProductType;
  status: string;
  price: string;
  regular_price: string;
  sale_price: string;
  sku: string;
  stock_status: string;
  stock_quantity: number | null;
  total_sales: number;
  categories: { id: string; name: string }[];
  images: { id: string; src: string; alt: string }[];
  description: string;
  short_description: string;
  date_created: string;
  /**
   * The product's last-modified stamp, sent back on the aggregate PATCH as the optimistic
   * concurrency token (`updatedAt`): a mismatch 409s `resource_stale` so a stale editor
   * refetches instead of last-write-winning. Optional — fixtures and reads without it
   * simply save token-less (last-write-wins, the pre-token behavior).
   */
  updated_at?: string;
  permalink: string;
  virtual: boolean;
  weight: string;
  /** True when CE `maxPerOrder` is 1 (WooCommerce "sold individually"). */
  sold_individually: boolean;
  downloads: { id: string; name: string; file: string }[];
  attributes: ShopProductAttribute[];
  cross_sell_ids: string[];
  /**
   * The generated per-variant rows (variable products), resolved from the full `/v1`
   * product. Empty for a simple/digital product and absent on the lighter list summary
   * (the form fetches the full product by id). Seeds the form's per-variant table and is
   * the pre-edit baseline the write path diffs against for the batch update. Optional so
   * the many list/fixture shapes that predate variants still satisfy the type; the mappers
   * always populate it (`[]` when none).
   */
  variants?: ShopProductVariant[];
  /**
   * The admin-only sellable-stock rollup (list summary only), or `null` when absent — the
   * full resource and older summaries omit it. Drives the list/drawer stock cell. Optional
   * for the same fixture-compatibility reason as {@link variants}.
   */
  stock_summary?: ShopStockSummary | null;
  /**
   * The number of sellable variants (1 for a simple product) — powers the "With {x} variants"
   * type badge and the card's variant line. From the summary's `variantCount` on the list, or
   * the full product's variant rows. Optional for fixture compatibility.
   */
  variant_count?: number;
  /**
   * The min–max regular-price span across the product's variants, as display strings.
   * The summary `price` is one REPRESENTATIVE variant (the oldest enabled one, not the
   * cheapest), so a variable product whose variants price differently renders this range
   * (`min – max`) instead of a single misleading figure. `null`/absent when the wire did
   * not carry it (older fixtures); equal min/max means every variant shares one price.
   */
  price_range?: { min: string; max: string } | null;
}

/**
 * A product category as consumed by the admin screens. `parent` is the parent
 * category id, or an empty string for a top-level category (the WooCommerce `0`
 * root convention carried over as `""` now that ids are UUID strings).
 */
export interface ShopCategory {
  id: string;
  name: string;
  slug: string;
  parent: string;
  description: string;
  count: number;
  image: { id?: string; src: string } | null;
}

/**
 * The write payload sent when creating or updating a category. New image uploads
 * send `{ src }` (the uploaded URL); an already-attached image sends `{ id, src }`;
 * `null` clears the image. `slug` is derived from the name server-side when omitted.
 */
export interface ShopCategoryPayload {
  name: string;
  slug?: string;
  parent: string;
  description?: string;
  image?: { id?: string; src: string } | null;
}

/**
 * A product image as written back to the API. New uploads send `{ src }` (the
 * uploaded URL); already-attached images send `{ id }` so the backend reuses the
 * existing media record. Exactly one of the two is set per entry.
 */
export type WcProductImageWrite =
  | { id: string; src?: undefined }
  | { src: string; id?: undefined };

/**
 * The write payload sent when creating or updating a product. Mirrors the
 * design-reviewed Website Builder v2 shape: form fields are mapped to the
 * backend's snake_case keys here so the screens never deal with API specifics.
 */
export interface ShopProductPayload {
  name: string;
  status: string;
  description: string;
  short_description: string;
  images: WcProductImageWrite[];
  virtual: boolean;
  downloads: { id: string; name: string; file: string }[];
  attributes: {
    name: string;
    options: ShopOptionValue[];
    visible: boolean;
    variation: boolean;
  }[];
  regular_price: string;
  sale_price: string;
  sku: string;
  weight: string;
  sold_individually: boolean;
  manage_stock: boolean;
  stock_quantity: number | null;
  stock_status: string;
  categories: { id: string }[];
  cross_sell_ids: string[];
}

/** Pagination, search, and sort parameters for {@link fetchProducts}. */
export interface FetchProductsParams {
  page?: number;
  perPage?: number;
  search?: string;
  /**
   * Publication-status filter — a single WooCommerce-style status (`"publish"`,
   * `"draft"`, `"private"`) or a comma-separated set for a multi-select. Each is
   * translated to the `/v1` lifecycle status and sent comma-separated (server-side).
   */
  status?: string;
  /** Comma-separated category ids. */
  category?: string;
  /**
   * Server-side stock-availability filter. `"instock"` keeps products with ≥1 enabled
   * variant in stock; `"outofstock"` keeps the rest. Fixes the page-1-only client filter.
   */
  availability?: 'instock' | 'outofstock';
  /**
   * Sort key. `title`/`price` map to the `/v1` `name`/`price`; `status`/`stock` are
   * admin-gated `/v1` keys (the server rejects `stock` for a storefront caller).
   */
  orderby?: 'title' | 'price' | 'status' | 'stock';
  order?: 'asc' | 'desc';
}

/** A billing or shipping address on an order. `email`/`phone` are billing-only. */
export interface ShopOrderAddress {
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  address_1: string;
  address_2: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
}

/** A single purchased line on an order. Monetary fields are pre-formatted strings. */
export interface ShopOrderLineItem {
  id: string;
  name: string;
  quantity: number;
  total: string;
  price: number;
  sku: string;
  /** Snapshotted line thumbnail URL, or null when the line has no image. */
  image: string | null;
  /**
   * Whether this line ships: a physical product line. Digital lines (delivered by
   * download grant) and fee lines never ship, so the Fulfil action excludes them.
   */
  shippable: boolean;
}

/**
 * An order as consumed by the admin screens. Monetary fields (`total`,
 * `total_tax`, …) are strings exactly as the WooCommerce-style API returns them,
 * so they are displayed verbatim with the shop currency symbol rather than parsed.
 */
export interface ShopOrder {
  id: string;
  number: string;
  status: string;
  /** The `/v1` payment status, surfaced only to gate the refund action. */
  payment_status: string;
  /**
   * The `/v1` fulfilment status, surfaced only to gate the Fulfil action (offered while
   * `unfulfilled`). Empty on the lighter list summary (the drawer reads the full order).
   */
  fulfilment_status: string;
  /**
   * The primary payment ledger type (`'manual' | 'shore' | ...`), surfaced to gate the
   * manual-workflow actions (Mark as paid, Fulfil). Empty on the list summary and when
   * the order has no payment yet.
   */
  payment_type: string;
  currency: string;
  currency_symbol: string;
  total: string;
  total_tax: string;
  discount_total: string;
  shipping_total: string;
  date_created: string;
  payment_method_title: string;
  customer_note: string;
  billing: ShopOrderAddress;
  shipping: ShopOrderAddress;
  line_items: ShopOrderLineItem[];
  coupon_lines: { code: string; discount: string }[];
}

/** Pagination, search, and status-filter parameters for {@link fetchOrders}. */
export interface FetchOrdersParams {
  page?: number;
  perPage?: number;
  /** A comma-joined status list (narrowed to the first for `/v1`); empty omits the filter. */
  status?: string;
  search?: string;
}

/**
 * A discount coupon as consumed by the admin screens, mirroring the design-
 * reviewed Website Builder v2 shape. The `id` is the `/v1` coupon UUID. Monetary
 * fields (`amount`, `minimum_amount`, `maximum_amount`) are decimal strings;
 * `usage_limit` / `usage_limit_per_user` are `null` when unlimited and
 * `date_expires` is `null` when the coupon never expires. Product/category
 * scoping (`/v1` `targets`) is not surfaced in this UI, so no scoping fields are
 * carried.
 */
export interface ShopCoupon {
  id: string;
  code: string;
  /** `"percent"`, `"fixed_cart"`, or `"fixed_product"`. */
  discount_type: string;
  amount: string;
  date_created: string;
  date_expires: string | null;
  usage_count: number;
  usage_limit: number | null;
  usage_limit_per_user: number | null;
  description: string;
  individual_use: boolean;
  free_shipping: boolean;
  minimum_amount: string;
  maximum_amount: string;
}

/**
 * The write payload sent when creating or updating a coupon. Mirrors the Website
 * Builder v2 mapping: `code`/`discount_type`/`amount` and the two toggles are
 * always sent, while the optional limits are omitted when blank (so the backend
 * keeps its default of "no limit"). `date_expires` carries an end-of-day time.
 */
export interface ShopCouponPayload {
  code: string;
  discount_type: string;
  amount: string;
  individual_use: boolean;
  free_shipping: boolean;
  date_expires?: string;
  usage_limit?: number;
  minimum_amount?: string;
  maximum_amount?: string;
  description?: string;
}

/** Pagination and search parameters for {@link fetchCoupons}. */
export interface FetchCouponsParams {
  page?: number;
  perPage?: number;
  search?: string;
}

/**
 * A custom shipping zone, mirroring the design-reviewed Website Builder v2 shape.
 * `order` is the display position (0-based); the zone's regions and methods are
 * fetched separately ({@link ShippingZoneLocation} / {@link ShippingMethod}).
 */
export interface ShippingZone {
  id: string;
  name: string;
  order: number;
}

/**
 * A region attached to a shipping zone. `type` is `"continent"`, `"country"`,
 * `"state"`, or `"all_regions"`; `code` is the matching WooCommerce code (a state
 * code is `"<country>:<state>"`, e.g. `"US:CA"`).
 */
export interface ShippingZoneLocation {
  code: string;
  type: string;
}

/**
 * A shipping method on a zone. `method_id` is `"flat_rate"` or `"free_shipping"`;
 * `settings` is the WooCommerce key → `{ value, label }` map (e.g. `cost`,
 * `requires`, `min_amount`), carried verbatim so the form can read and write it.
 */
export interface ShippingMethod {
  instance_id: string;
  title: string;
  method_id: string;
  method_title: string;
  enabled: boolean;
  settings: Record<string, { value: string; label: string }>;
}

/** The write payload for updating a zone method (enabled flag, title, settings, order). */
export interface ShippingZoneMethodUpdate {
  enabled?: boolean;
  title?: string;
  /** The method's position within its zone; sent on save so a method reorder persists. */
  sortOrder?: number;
  /** Flat key → value settings (WooCommerce write shape), e.g. `{ cost: "5.00" }`. */
  settings?: Record<string, string>;
}

/**
 * The region tree as consumed by the region selector: a continent with its
 * countries, each carrying its states. Built on the client from bundled static
 * datasets (see `shipping/region-data.ts`); display names are localized per
 * locale (see `shipping/region-localization.ts`).
 */
export interface ShopContinent {
  code: string;
  name: string;
  countries: {
    code: string;
    name: string;
    states: { code: string; name: string }[];
  }[];
}

/** A pickup location's address (a subset of the WooCommerce address fields). */
export interface PickupLocationAddress {
  address_1: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
}

/**
 * A pickup location backed by the real `/v1` API (id-based, with an explicit display
 * order): rows are addressed by `id`, drag-reorder persists `sortOrder`. There is no
 * per-location `enabled` flag (deleting the location expresses "pickup not offered" —
 * see backend-gaps Gap 8).
 */
export interface ShopPickupLocation {
  id: string;
  name: string;
  address: PickupLocationAddress;
  details: string;
  sortOrder: number;
}

/**
 * The editable fields of a pickup location (the section/form surface): no `id` or
 * `sortOrder` (the engine assigns those; drag-reorder owns the order).
 */
export interface PickupLocationInput {
  name: string;
  address: PickupLocationAddress;
  details: string;
}

/**
 * The shop settings surface for the shop-level business settings edited on the Settings
 * tab. `title` / `email` / `language` / `date_format` plus the optional address, currency,
 * and unit fields below are the Settings-form surface. The optional fields are absent on
 * older payloads, so every consumer must tolerate `undefined`. (Pickup locations moved to
 * the `/v1` shipping API — see {@link ShopPickupLocation} — and are no longer carried here.)
 */
export interface ShopSettings {
  title: string;
  email: string;
  /** Outgoing-email locale code (e.g. `"en_US"`); mapped to the Email language field. */
  language: string;
  /** PHP date-format string (e.g. `"F j, Y"`); mapped to the Date format field. */
  date_format: string;
  street_address?: string;
  city?: string;
  postcode?: string;
  /** ISO 3166-1 alpha-2 country code (e.g. `"US"`). */
  country?: string;
  /** ISO 4217 currency code (e.g. `"USD"`). */
  currency?: string;
  /** `"left" | "left_space" | "right" | "right_space"`. */
  currency_position?: string;
  /** Pipe-delimited `thousandSep|decimalSep|numDecimals` (e.g. `",|.|2"`). */
  number_format?: string;
  /** `"g" | "kg" | "lbs" | "oz"`. */
  weight_unit?: string;
  /** BCP-47 shop/shopfront locale (e.g. `"en-GB"`); the storefront/default language. */
  locale?: string;
  /** IANA time zone (e.g. `"Europe/Copenhagen"`); order dates/emails render in it. */
  timezone?: string;
}
